import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useWorks } from '../contexts/WorksContext';
import { Work } from '../models/Work';

// Create a slug from title
export const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '-');
};

const ENSEMBLE_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'for',
  'in',
  'of',
  'the',
  'to',
  'with',
]);

const ENSEMBLE_SUFFIX_OVERRIDES: Record<string, string> = {
  keyboard: '4-hand',
};

const SCORE_FILENAME_OVERRIDES: Record<string, string> = {
  'willow-dreams-at-dawn': 'willowdreams-at-dawn-',
};

const AVAILABLE_PERUSAL_SCORE_FILES = new Set([
  'willowdreams-at-dawn-.pdf',
  'urban-breakdown.pdf',
  'three-nursery-rhymes.pdf',
  'there-is-no-rose.pdf',
  'the-st-john-passion.pdf',
  'the-heart-of-lancashire.pdf',
  'terra-tremuit.pdf',
  'summoning-dance.pdf',
  'shine-out-fair-sun.pdf',
  'rise-up-my-love-my-fair-one.pdf',
  'relativitt.pdf',
  'o.pdf',
  'o-wonder.pdf',
  'o-sacrum-convivium.pdf',
  'o-lux-beatissima.pdf',
  'missa-pro-pnitentia.pdf',
  'meditatio.pdf',
  'meditabor-in-mandatis-tuis.pdf',
  'love-an-everfixed-mark.pdf',
  'i-cannot-go.pdf',
  'hush-my-dear-lie-still-and-slumber.pdf',
  'gloria.pdf',
  'fanfare-to-a-haggis.pdf',
  'called-on-by-light.pdf',
  'ave-regina-caelorum.pdf',
  'ave-maria.pdf',
  'a-voice-is-heard-in-ramah.pdf',
  'a-summer-evening-of-music.pdf',
  'a-glimpse-of-the-light-orchestra.pdf',
  'a-glimpse-of-the-light-4-hand.pdf',
  '4-schiller-vierzeiler.pdf',
  '3-naturstcke.pdf',
]);

const createEnsembleSuffix = (ensembleType?: string): string => {
  if (!ensembleType) {
    return '';
  }

  const cleanedWords = ensembleType
    .toLowerCase()
    .split(/\s+/)
    .map(word => word.replace(/[^\w-]/g, ''))
    .filter(Boolean)
    .filter(word => !ENSEMBLE_STOP_WORDS.has(word));

  const normalized = cleanedWords.join('-');
  return ENSEMBLE_SUFFIX_OVERRIDES[normalized] || normalized;
};

const createPerusalScoreUrl = (work: Work, works: Work[]): string | undefined => {
  const titleSlug = createSlug(work.title);
  const sameTitleCount = works.filter(candidate => createSlug(candidate.title) === titleSlug).length;
  let scoreBaseSlug = titleSlug;

  if (sameTitleCount >= 2) {
    const ensembleSuffix = createEnsembleSuffix(work.ensembleType);
    if (!ensembleSuffix) {
      return undefined;
    }
    scoreBaseSlug = `${titleSlug}-${ensembleSuffix}`;
  }

  const scoreFileNameBase = SCORE_FILENAME_OVERRIDES[scoreBaseSlug] || scoreBaseSlug;
  const scoreFileName = `${scoreFileNameBase}.pdf`;

  if (!AVAILABLE_PERUSAL_SCORE_FILES.has(scoreFileName)) {
    return undefined;
  }

  return `/perusal_scores/${scoreFileName}`;
};

// Helper function to find work by slug
export const findWorkBySlug = (works: Work[], slug: string): Work | undefined => {
  return works.find(work => createSlug(work.title) === slug);
};

const DetailContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 0 45px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: #666;
  cursor: pointer;
  margin-bottom: 22.5px;
  display: flex;
  align-items: center;
  
  &:before {
    content: '←';
    margin-right: 7.5px;
  }
  
  &:hover {
    color: #000;
  }
`;

const Title = styled.h1`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.8rem;
  color: #333;
  margin: 0 0 15px;
  line-height: 1.3;
`;

const MetaSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18.75px;
  margin-bottom: 30px;
`;

const MetaItem = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: #444;
  background-color: #f9f9f9;
  padding: 7.5px 11.25px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  
  &:before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background-color: #999;
    border-radius: 50%;
    margin-right: 7.5px;
  }
`;

const Section = styled.section`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 15px;
`;

const Description = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.825rem;
  line-height: 1.6;
  color: #444;
  margin: 0 0 15px;
`;

const DetailItem = styled.div`
  margin-bottom: 11.25px;
  font-family: 'Inter', sans-serif;
  font-size: 0.825rem;
  line-height: 1.5;
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #555;
  display: block;
  margin-bottom: 3.75px;
`;

const MediaSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 15px;
`;

const MediaCard = styled.a`
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 11.25px;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f0f0f0;
    transform: translateY(-1.5px);
  }
`;

const MediaIcon = styled.div`
  width: 30px;
  height: 30px;
  background-color: #333;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.675rem;
`;

const MediaLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: #444;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 0;
  color: #666;
  font-family: 'Inter', sans-serif;
  font-size: 0.825rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 45px 0;
  color: #666;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  background-color: #f9f9f9;
  border-radius: 6px;
`;

const WorkDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { works, loading } = useWorks();
  const navigate = useNavigate();
  const [work, setWork] = useState<Work | null>(null);
  const baseTitle = "Rory Wainwright Johnston";
  const tagline = "Conductor · Composer";
  
  // Effect to find the work data based on slug
  useEffect(() => {
    if (!loading && slug) {
      const foundWork = findWorkBySlug(works, slug);
      setWork(foundWork || null); // Set to null if not found
    }
  }, [loading, works, slug]);

  // Effect to update document title when work data is loaded
  useEffect(() => {
    let pageName = "Work Not Found"; 
    if (work) {
      pageName = work.title; 
    } else if (loading) {
       return; 
    }
    
    // Use new format: Page | Base | Tagline
    document.title = `${pageName} | ${baseTitle} | ${tagline}`; 
    
    // TODO: Optionally update meta description here too?
    // let metaDescription = document.querySelector('meta[name="description"]');
    // if (metaDescription) {
    //    metaDescription.setAttribute('content', `Details for the work '${pageName}' by ${baseTitle}.`);
    // }

  }, [work, loading]);

  const handleBack = () => {
    navigate('/works');
  };

  if (loading) {
    return <LoadingMessage>Loading work details...</LoadingMessage>;
  }

  if (!work) {
    return (
      <DetailContainer>
        <BackButton onClick={handleBack}>Back to Works</BackButton>
        <ErrorMessage>Work not found.</ErrorMessage>
      </DetailContainer>
    );
  }

  const resolvedScoreUrl = createPerusalScoreUrl(work, works);

  return (
    <DetailContainer>
      <BackButton onClick={handleBack}>Back to Works</BackButton>
      
      <Title>{work.title}</Title>
      
      <MetaSection>
        <MetaItem>{work.category}</MetaItem>
        {work.ensembleType && <MetaItem>{work.ensembleType}</MetaItem>}
        {work.year && <MetaItem>{work.year}</MetaItem>}
        {work.duration && <MetaItem>{work.duration}</MetaItem>}
      </MetaSection>
      
      <Section>
        <SectionTitle>Composition Details</SectionTitle>
        
        <DetailItem>
          <DetailLabel>Instrumentation</DetailLabel>
          {work.generalInstrumentation}
        </DetailItem>
        
        {work.detailedInstrumentation && (
          <DetailItem>
            <DetailLabel>Detailed Instrumentation</DetailLabel>
            {work.detailedInstrumentation}
          </DetailItem>
        )}
        
        {work.premiere && (
          <DetailItem>
            <DetailLabel>Premiere</DetailLabel>
            {work.premiere}
          </DetailItem>
        )}
        
        {work.commissioner && (
          <DetailItem>
            <DetailLabel>Commissioned by</DetailLabel>
            {work.commissioner}
          </DetailItem>
        )}
      </Section>
      
      {work.description && (
        <Section>
          <SectionTitle>Description</SectionTitle>
          <Description>{work.description}</Description>
        </Section>
      )}
      
      {(resolvedScoreUrl || work.videoUrl || work.soundcloudUrl) && (
        <Section>
          <SectionTitle>Media</SectionTitle>
          
          <MediaSection>
            {resolvedScoreUrl && (
              <MediaCard href={resolvedScoreUrl} target="_blank" rel="noopener noreferrer">
                <MediaIcon>S</MediaIcon>
                <MediaLabel>View Score</MediaLabel>
              </MediaCard>
            )}
            
            {work.videoUrl && (
              <MediaCard href={work.videoUrl} target="_blank" rel="noopener noreferrer">
                <MediaIcon>V</MediaIcon>
                <MediaLabel>Watch Performance</MediaLabel>
              </MediaCard>
            )}
            
            {work.soundcloudUrl && (
              <MediaCard href={work.soundcloudUrl} target="_blank" rel="noopener noreferrer">
                <MediaIcon>A</MediaIcon>
                <MediaLabel>Listen to Recording</MediaLabel>
              </MediaCard>
            )}
          </MediaSection>
        </Section>
      )}
    </DetailContainer>
  );
};

export default WorkDetail; 